/* ==========================================================================
   market.js — market resolution, switching, and content rendering
   --------------------------------------------------------------------------
   Resolution order: ?market= → saved preference → storefront country (UK)
   → timezone → language → storefront country → default.
   Renders all localized strings + the full "where to buy" directory.
   Exposes window.NATY.market { current, set, onChange }.
   ========================================================================== */

(function () {
  const { MARKETS, SETTINGS } = window.NATY;
  const STORAGE_KEY = 'naty.market';
  const listeners = [];
  let current = null;

  /* --- Resolution ---
     The storefront's own country is only decisive for the UK, which has a
     dedicated /en-gb URL. Every other country shares the root URL, so the
     country reported there says little about a visitor that URL doesn't serve
     (e.g. a UK visitor landing on /pages/stockists) — there the device's
     timezone and language are the better signal and Shopify's is a last resort. */
  const TZ = [
    ['uk', /^Europe\/(London|Belfast|Jersey|Guernsey|Isle_of_Man)$/],
    ['de', /^Europe\/(Berlin|Busingen)$/],
    ['us', /^(America\/(New_York|Detroit|Chicago|Menominee|Denver|Boise|Phoenix|Los_Angeles|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak|Indiana\/.+|Kentucky\/.+|North_Dakota\/.+)|Pacific\/Honolulu)$/],
  ];

  function byCountry(iso) {
    const hit = Object.values(MARKETS).find((m) => m.country === iso);
    return hit ? hit.code : null;
  }

  function resolve() {
    const param = new URLSearchParams(location.search).get('market');
    if (param && MARKETS[param.toLowerCase()]) return param.toLowerCase();

    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (e) { /* storage blocked */ }
    if (saved && MARKETS[saved]) return saved;

    const shopCountry = (window.NATY_ASSETS && window.NATY_ASSETS.country) || '';
    if (shopCountry === 'GB') return 'uk';

    let tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { /* no Intl */ }
    const zone = TZ.find(([, re]) => re.test(tz));
    if (zone) return zone[0];

    const lang = (navigator.language || '').toLowerCase();
    if (lang === 'de' || lang.startsWith('de-')) return 'de';
    if (lang === 'en-gb') return 'uk';
    if (lang === 'en-us') return 'us';

    return byCountry(shopCountry) || SETTINGS.defaultMarket;
  }

  /* --- Apply localized strings to [data-str] / [data-str-attr] nodes --- */
  function applyStrings(strings) {
    document.querySelectorAll('[data-str]').forEach((el) => {
      const key = el.getAttribute('data-str');
      if (strings[key] != null) el.textContent = strings[key];
    });
    document.querySelectorAll('[data-str-attr]').forEach((el) => {
      const [key, attr] = el.getAttribute('data-str-attr').split(':');
      if (strings[key] != null) el.setAttribute(attr, strings[key]);
    });
  }

  /* --- Build a single retailer card --- */
  function retailerCard(retailer, market, type, isAmazon) {
    const a = document.createElement('a');
    a.className = 'retailer' + (isAmazon ? ' retailer--amazon' : '');
    a.href = retailer.url;
    a.target = '_blank';
    a.rel = 'noopener';
    const copy = document.createElement('span');
    copy.className = 'retailer__copy';
    if (isAmazon) {
      const tag = document.createElement('span');
      tag.className = 'retailer__tag';
      tag.textContent = market.strings.amazonTag;
      copy.appendChild(tag);
    }
    const nameEl = document.createElement('span');
    nameEl.className = 'retailer__name';
    nameEl.textContent = retailer.name;
    const logoFile = !isAmazon && window.NATY.LOGOS[retailer.name];
    if (logoFile) {
      const img = document.createElement('img');
      img.className = 'retailer__logo';
      img.src = window.NATY_ASSETS.logos[logoFile];
      img.alt = retailer.name;
      img.loading = 'lazy';
      img.onerror = () => img.replaceWith(nameEl); // graceful fallback to text
      copy.appendChild(img);
    } else {
      copy.appendChild(nameEl);
    }
    a.appendChild(copy);
    const go = document.createElement('span');
    go.className = 'retailer__go';
    go.setAttribute('aria-hidden', 'true');
    go.textContent = '↗';
    a.appendChild(go);
    a.addEventListener('click', () => {
      if (isAmazon) window.NATY.track.amazonClick(market.code);
      else window.NATY.track.retailerClick(market.code, retailer.name, type);
    });
    return a;
  }

  /* --- Render the full directory (In store + Online, Amazon featured) --- */
  function renderDirectory(market) {
    const inStore = document.querySelector('[data-instore-grid]');
    const online = document.querySelector('[data-online-grid]');
    inStore.innerHTML = '';
    online.innerHTML = '';

    market.physicalChains.forEach((r) =>
      inStore.appendChild(retailerCard(r, market, 'in_store', false))
    );

    // Amazon leads the Online group.
    online.appendChild(
      retailerCard({ name: 'Amazon', url: market.amazonUrl }, market, 'online', true)
    );
    market.onlineRetailers.forEach((r) =>
      online.appendChild(retailerCard(r, market, 'online', false))
    );
  }

  /* --- Update the switcher button + selected state --- */
  function renderSwitcher(market) {
    const flag = document.querySelector('[data-switch-flag]');
    const label = document.querySelector('[data-switch-label]');
    if (flag) flag.textContent = market.flag;
    if (label) label.textContent = market.label;
    document.querySelectorAll('[data-market-option]').forEach((li) => {
      li.setAttribute('aria-selected', li.getAttribute('data-market-option') === market.code);
    });
  }

  /* --- Set the active market everywhere --- */
  function set(code, persist = true) {
    const market = MARKETS[code];
    if (!market) return;
    current = market;
    if (persist) { try { localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* storage blocked */ } }

    document.documentElement.setAttribute('data-market', code);
    document.documentElement.setAttribute('lang', market.locale.split('-')[0]);

    applyStrings(market.strings);

    // Hero Amazon CTA href + click tracking
    const cta = document.querySelector('[data-amazon-cta]');
    if (cta) {
      cta.href = market.amazonUrl;
      cta.onclick = () => window.NATY.track.amazonClick(market.code);
    }

    renderDirectory(market);
    renderSwitcher(market);
    listeners.forEach((fn) => fn(market));
  }

  window.NATY.market = {
    get current() { return current; },
    set,
    resolve,
    onChange: (fn) => listeners.push(fn),
  };
})();
